// @flow

import { featureIsEnabled } from '@ta/common.features';

/**
 * Legacy method to support URL munging because the recent API doesn't return links relative to the current context
 * (i.e. hotels, rest, attr, etc.). This should be handled in the API but it isn't currently.
 */
const getSearchUri = (uri, geo) => {
  // eslint-disable-next-line no-restricted-globals
  const urlParams = new URLSearchParams(document.location.search.substring(1));
  const query = urlParams.get('q');
  if (query) {
    const params = {
      q: query,
      geo,
    };
    const paramString = new URLSearchParams(params).toString();
    return `/Search?${paramString}`;
  }
  // if there is no query for some reason, let's just fallback
  return uri;
};

/**
 * Extract the servlet name from a uri
 */
export const getServlet = (uri: string): ?string => {
  const match = uri.match(/(\/[\w_]+).*/);
  return match && match.length > 1 ? match[1] : null;
};

/**
 * Flights pages should redirect to /Flights-g, and if an origin ID can be found, add it to the URL too
 */
const getFlightsUri = geo => {
  let uri = `/Flights-g${geo}`;
  if (sessionStorage) {
    const origID = sessionStorage.getItem('origID');
    if (origID) {
      uri = `${uri}-o${origID}`;
    }
  }

  return uri;
};

const getShoppingUri = (oldUrl, uri, geo) => {
  const match = oldUrl.match(/-c(\d+)-/);
  const attractionCategory = match && match.length > 1 ? match[1] : null;
  if (attractionCategory === '26') {
    return `/Attractions-g${geo}-c26`;
  }
  return uri;
};

/**
 * Legacy support method to build a URL to change the current context to nearby based on the current URL. For example
 * it will convert a /Hotels-g60745 URL to /MobileNearbyHotels.
 *
 * In theory we want this URL to actually come from an API but until that support exists we are stuck querying
 * window.location.
 *
 * This (and other methods) were ported from site/placements/global_nav_geopill/deferred/GeoSearch.js
 */
export const legacyBuildNearbyUrl = (latitude: number, longitude: number): string => {
  /* eslint-disable no-restricted-globals */
  const pathName = window.location.pathname || '/';
  const searchPath = window.location.search || '';
  /* eslint-enable no-restricted-globals */

  const servlet = pathName
    .match(/\/(\w*)/)[1]
    .replace('MobileRestaurantSearch', 'Restaurants')
    .replace('Attraction_Products', 'Attractions')
    .replace(/(Hotel|Attraction|Restaurant)_Review/, '$1s')
    .replace(/(Restaurants|Attractions|Hotels)Near/, '$1')
    .replace(/Mobile(Hotel|Restaurant)Search/, '$1s')
    .replace(/(HotelHighlight|SmartDeals|HotelsList|MobileMetaHac|LastMinute)/, 'Hotels')
    .replace('Tourism', 'Home');

  let uri = `/${servlet}`;
  if (servlet.indexOf('MobileNearby') !== -1) {
    uri = `${servlet}?j=distLow`;
  } else if (['Hotels', 'Restaurants', 'Attractions'].indexOf(servlet) >= 0) {
    uri = `/MobileNearby${servlet}?j=distLow`;
  } else if (['VacationRentals', 'MobileVacationRentalSearch'].indexOf(servlet) >= 0) {
    uri = '/MobileVacationRentalSearch?j=distLow';
  } else if (servlet === 'Search') {
    uri = `${(pathName + searchPath).replace(
      /&?(latitude|longitude|searchNearby|geo)=[^&]*/g,
      '',
    )}&searchNearby=true`;
  }

  return `${uri}${uri.includes('?') ? '&' : '?'}latitude=${encodeURIComponent(
    `${latitude}`,
  )}&longitude=${encodeURIComponent(`${longitude}`)}&isNearby=true`;
};

/**
 * Legacy support method to build a URL to for a typeahead search result which goes to a page that is geo-scoped to a
 * given geo but is based on the current URL. For example we maybe have a geoID of 60745 (boston) but since we are on
 * the /ShowTopic page the typeahead should bring us to /ShowForum-g60745.
 *
 * In theory we really want to provide a param to the API (such as the 'link_type' param to /TypeaheadJson?action=API)
 * to indicate what kind of links we want in the results but unfortunately there is currently no API that supports all
 * the possible link types.
 *
 * This (and other methods) were ported from site/placements/global_nav_geopill/deferred/GeoSearch.js
 */
export const buildUrl = (geo: string, overrideDefaultServlet?: string) => {
  if (!geo || geo.length === 0) {
    return '/';
  }

  // eslint-disable-next-line no-restricted-globals
  const oldUrl = window.location.pathname;
  const oldServlet = getServlet(oldUrl);

  let uri = oldUrl
    .replace('_Review', 's')
    .replace(/\/(ForumFAQAdmin|ForumItineraryAdmin|ShowTopic)-g\d+-.*/, '/ShowForum-g')
    .replace(/-g.*/, `-g${geo}`)
    .replace(/\/(MobileRestaurantSearch).*/, `/$1-g${geo}`)
    .replace(/\/AttractionProductReview.*/, `/Attraction_Products-g${geo}`)
    .replace(/\/HotelsList.*/, `/Hotels-g${geo}`)
    .replace(/\/MobileNearby(\w+).*/, `/$1-g${geo}`)
    .replace(/\/MobileMetaHac/, `/Hotels-g${geo}`)
    .replace(/\/Neighborhood-g.*/, `/NeighborhoodList-g${geo}`);

  // Add search result page specific url logic
  if (oldServlet === '/Search') {
    uri = getSearchUri(uri, geo);
  }

  // Add flights page specific url logic
  if (oldServlet && oldServlet.match('/Flights.*')) {
    uri = getFlightsUri(geo);
  }
  // Maintain the filter for shopping pages
  if (oldServlet === '/Attractions') {
    uri = getShoppingUri(oldUrl, uri, geo);
  }

  // fallback to Tourism if we can't rebuild the URL
  if (uri === oldUrl) {
    if (overrideDefaultServlet) {
      uri = `${overrideDefaultServlet}-g${geo}`;
    } else if (featureIsEnabled('navigation_to_scoped_home')) {
      uri = `/Home-g${geo}`;
    } else {
      uri = `/Tourism-g${geo}`;
    }
  }
  return uri;
};

export default buildUrl;
